import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box,
  IconButton,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTime } from 'luxon';
import { type FC, useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';

import LogDelete from './LogDelete.js';
import LogFormDialog from './LogFormDialog.js';
import LogsTable from './LogsTable.js';
import {
  type DailyReport,
  type LogSource,
  useCheckInReportForPatient,
  useDailyReportForPatient,
  useLogsForPatient,
  usePatient,
} from '../../api/index.js';
import { useCheckPermissions } from '../../auth/index.js';
import useDialogControls from '../../hooks/use-dialog-controls.js';
import { type Log } from '../../models/index.js';
import { Events, track } from '../../utils/index.js';
import { PatientCheckInReport, PatientLogsReport } from '../Report/index.js';

interface LogsContainerProps {
  patientId?: string;
}

const LogsContainer: FC<LogsContainerProps> = ({ patientId }) => {
  const [startDate, setStartDate] = useState<DateTime>(DateTime.now().minus({ week: 1 }).startOf('week'));
  const [endDate, setEndDate] = useState<DateTime>(DateTime.now().minus({ week: 1 }).endOf('week'));
  const [activeTab, setActiveTab] = useState<0 | 1 | 2>(0);
  const [logSource, setLogSource] = useState<LogSource>('user');
  const [hasFetched, setHasFetched] = useState(false);
  const [isPatientNameVisible, setIsPatientNameVisible] = useState(true);
  const reportComponentRef = useRef<HTMLDivElement | null>(null);
  const handlePrint = useReactToPrint({
    content: () => reportComponentRef.current,
  });
  const patient = usePatient(patientId, {
    includeCameras: true,
    includeEmergencyContacts: true,
  }, { enabled: !!patientId });
  const patientLogs = useLogsForPatient(patientId!, startDate, endDate, {
    onSuccess: () => {
      track(Events.ReportGeneration, {
        patientId,
      });
    },
    enabled: false,
  });
  const dailyReport = useDailyReportForPatient(patientId!, startDate, endDate, logSource, {
    enabled: false,
  });
  const checkInReport = useCheckInReportForPatient(patientId!, startDate, endDate, logSource, {
    enabled: false,
  });

  const {
    open: formOpen,
    selectedData: formData,
    onOpen: onFormOpen,
    reset: resetForm,
    setOpen: setFormOpen,
  } = useDialogControls<Log>(patientLogs?.data?.logs as Log[]);
  const {
    open: deleteOpen,
    selectedData: deleteData,
    onOpen: onDelete,
    reset: resetDelete,
  } = useDialogControls<Log>(patientLogs?.data?.logs as Log[]);

  const isCheckInTabDisabled = !checkInReport.data && !checkInReport.isError;

  useEffect(() => {
    if ((activeTab === 1 && !dailyReport?.data?.report?.length) || (activeTab === 2 && isCheckInTabDisabled)) {
      setActiveTab(0);
    }
  }, [activeTab, dailyReport, isCheckInTabDisabled]);

  // Changing the source swaps the report query keys, so refetch rather than
  // leave the already-open Report/Check-in tab staring at an empty cache.
  useEffect(() => {
    if (hasFetched) {
      dailyReport.refetch();
      checkInReport.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logSource]);
  const { canManageLog } = useCheckPermissions();

  const checkInReportView = () => {
    if (checkInReport.isError) {
      return (
        <Alert severity={'error'}>Could not load the check-in report. Try again or pick a different date range.</Alert>
      );
    }
    if (!checkInReport.data?.report) {
      return <Alert severity={'info'}>No check-in schedule in the selected date range.</Alert>;
    }
    return (
      <PatientCheckInReport
        data={checkInReport.data}
        showPatientName={isPatientNameVisible}
        ref={reportComponentRef}
      />
    );
  };

  const viewBasedOnAction = () => {
    switch (activeTab) {
      case 0:
        return (
          <LogsTable data={patientLogs?.data} isLoading={patientLogs.isFetching} onEdit={onFormOpen}
                     onDelete={onDelete} />
        );
      case 1:
        return dailyReport?.data ? (
          <PatientLogsReport
            data={dailyReport?.data as DailyReport}
            showPatientName={isPatientNameVisible}
            ref={reportComponentRef}
          />
        ) : null;
      case 2:
        return checkInReportView();
    }
  };

  return (
    <>
      <Stack direction={'row'} alignItems={'center'}>
        <Typography variant={'body1'}>Timezone: {patient?.data?.timeZone || patientLogs?.data?.timeZone}</Typography>
        {canManageLog ? (
          <Tooltip arrow color="primary" placement="bottom" title={'Add a new log'} sx={{ ml: 'auto' }}>
            <Box>
              <IconButton
                color="inherit"
                aria-label="Add a new log"
                onClick={() => setFormOpen(true)}
                disabled={!patientId}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Tooltip>
        ) : null}
      </Stack>
      <Stack spacing={1} direction={'row'} alignItems={'center'} sx={{ my: 2 }}>
        <DatePicker
          formatDensity={'spacious'}
          slotProps={{ textField: { size: 'small' } }}
          value={startDate}
          onChange={(value) => {
            if (value) {
              setStartDate(value.setZone(patientLogs?.data?.timeZone, { keepLocalTime: true }));
            }
          }}
          label={'From'}
          disableFuture
        />
        <DatePicker
          formatDensity={'spacious'}
          slotProps={{ textField: { size: 'small' } }}
          value={endDate}
          onChange={(value) => {
            if (value) {
              setEndDate(value?.setZone(patientLogs?.data?.timeZone, { keepLocalTime: true }));
            }
          }}
          label={'To'}
          disableFuture
          {...{ ...(startDate && { minDate: startDate }) }}
        />
        <Tooltip arrow placement={'bottom'} title={'Which logs the Report and Check-in tabs count'}>
          <ToggleButtonGroup
            size={'small'}
            exclusive
            value={logSource}
            onChange={(_, value: LogSource | null) => {
              if (value) {
                setLogSource(value);
              }
            }}
          >
            <ToggleButton value={'user'}>User</ToggleButton>
            <ToggleButton value={'ai'}>AI</ToggleButton>
            <ToggleButton value={'all'}>Both</ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
        <LoadingButton
          variant={'contained'}
          onClick={() => {
            setHasFetched(true);
            patientLogs.refetch();
            dailyReport.refetch();
            checkInReport.refetch();
          }}
          disabled={!patientId}
          loading={patientLogs.isFetching || dailyReport.isFetching || checkInReport.isFetching}
        >
          Get Logs & Reports
        </LoadingButton>
        <IconButton onClick={handlePrint} color={'primary'} disabled={activeTab === 0}>
          <PrintIcon />
        </IconButton>
        <IconButton
          onClick={() => setIsPatientNameVisible((prevState) => !prevState)}
          color={'primary'}
          disabled={activeTab === 0}
        >
          {isPatientNameVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </Stack>
      <Box sx={{ width: '100%', bgcolor: 'background.paper', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} centered>
          <Tab label="Logs" />
          <Tab label="Report" disabled={!dailyReport?.data?.report?.length} />
          <Tab label="Check-in" disabled={isCheckInTabDisabled} />
        </Tabs>
      </Box>
      {viewBasedOnAction()}
      <LogFormDialog
        open={formOpen}
        onClose={resetForm}
        log={formData}
        refetch={patientLogs.refetch}
        patientId={patientId}
      />
      <LogDelete
        open={deleteOpen}
        onClose={resetDelete}
        log={deleteData}
        refetch={patientLogs.refetch}
        timezone={patient?.data?.timeZone || patientLogs?.data?.timeZone}
      />
    </>
  );
};

export default LogsContainer;
